import type { ReactNode } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { BrowserPopover, type PopoverCloseReason } from '../BrowserPopover';
import { useBrowserPopover } from '../useBrowserPopover';

interface FilterChipProps {
    label: string;
    value: string;
    children: (close: (reason: PopoverCloseReason) => void) => ReactNode;
}

export function FilterChip({ label, value, children }: FilterChipProps) {
    const popover = useBrowserPopover();

    return (
        <div ref={popover.anchorRef} className="relative">
            <button
                ref={popover.triggerRef}
                type="button"
                onClick={popover.toggle}
                aria-expanded={popover.isOpen}
                aria-controls={popover.isOpen ? popover.popoverId : undefined}
                aria-haspopup="dialog"
                className={`inline-flex h-[30px] items-center gap-1.5 rounded-full border bg-white pl-3 pr-2.5 text-[12px] text-gray-500 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 ${
                    popover.isOpen ? 'border-gray-400' : 'border-gray-200 hover:border-gray-300'
                }`}
            >
                {label}
                <span className="font-semibold text-gray-900">{value}</span>
                <ChevronDownIcon
                    className={`h-3.5 w-3.5 text-gray-400 transition-transform ${popover.isOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                />
            </button>
            {popover.isOpen && (
                <BrowserPopover
                    id={popover.popoverId}
                    label={label}
                    anchorRef={popover.anchorRef}
                    // The chips sit at the toolbar's right end, so menus open leftwards inside the modal.
                    align="right"
                    onClose={popover.close}
                >
                    {children(popover.close)}
                </BrowserPopover>
            )}
        </div>
    );
}
